import { Backdrop, Box, Container, FormControl } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useAppDispatch, useAppSelector } from "app/hooks";
import OutlinedButton from "components/buttons/OutlinedButton";
import Header from "components/header/Header";
import { addCertificate, removeLastCertificate, selectCertificateById, updateCertificate } from "features/seller/newSellerSlice";
import { patchSellerCertificates, addSellerCertificate, removeLastSellerCertificate, selectSellerCertificateById, updateSellerCertificate } from "features/seller/sellerSlice";
import { selectUserRole } from "features/user/userSlice";
import { NewCertificateInterface } from "interfaces/Certificate";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LotOfferButton from "../lotOffer/LotOfferButton";
import CertificateForm from "./CertificateForm";
import ValidationErrors from "components/modals/ValidationErrors";

export default function CertificateSave() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { certificate_id, seller_id } = useParams<{ certificate_id?: string, seller_id?: string }>();
  const userRole = useAppSelector(selectUserRole);

  const isEditMode = !!certificate_id;
  const existingCertificate = useAppSelector(state =>
    certificate_id ?
      (seller_id ?
        selectSellerCertificateById(state, certificate_id) :
        selectCertificateById(state, certificate_id)) :
      undefined
  );

  const [certificateTypeId, setCertificateType] = useState<string>("");
  const [certificateNumber, setCertificateNumber] = useState<string>("");
  const [validationInfo, setValidationInfo] = useState<string[]>([]);
  const [logoRegNumber, setLogoRegNumber] = useState<string>("");
  const [validFrom, setValidFrom] = useState<string>(DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISO());

  const [validTo, setValidTo] = useState<string>(DateTime.now().plus({ day: 1 }).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISO());
  const [isFormValid, setIsFormValid] = useState<boolean>(true);

  useEffect(() => {
    if (isEditMode && existingCertificate) {
      setCertificateType(existingCertificate.certificate_type_id || "");
      setCertificateNumber(existingCertificate.certificate_number || "");
      setLogoRegNumber(existingCertificate.logo_registration_number || "");
      setValidFrom(existingCertificate.valid_from || "");
      setValidTo(existingCertificate.valid_to || "");
      setIsFormValid(true);
    } else {
      setCertificateType("");
      setCertificateNumber("");
      setLogoRegNumber("");
      setIsFormValid(false);
    }
  }, [isEditMode, existingCertificate]);

  const handleFormValidationChange = (isValid: boolean, error?: string | null) => {
    setIsFormValid(isValid);
  };

  const saveChanges = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if(!canSubmit) return;

    const certificateData: Omit<NewCertificateInterface, "id" | "_destroy"> = {
      certificate_type_id: certificateTypeId,
      certificate_number: certificateNumber,
      logo_registration_number: logoRegNumber,
      valid_from: validFrom,
      valid_to: validTo,
    };

    let isNewCertifacate = 0;
    if (isEditMode && existingCertificate) {
      // Check if we have a seller_id
      if (seller_id) {
        // If we have a seller_id, we need to update the certificate in the seller's list
        await dispatch(updateSellerCertificate({ ...certificateData, id: existingCertificate.id }));
      } else {
        await dispatch(updateCertificate({ ...certificateData, id: existingCertificate.id }));
      }
    } else {
      if (seller_id) {
        await dispatch(addSellerCertificate({ ...certificateData, uuid: crypto.randomUUID() }));
        isNewCertifacate = 1;
      } else {
        await dispatch(addCertificate({ ...certificateData, uuid: crypto.randomUUID() }));
        isNewCertifacate = 2;
      }
    }
    const result = await dispatch(patchSellerCertificates());
    const { data, status } = result.payload as { data: any, status: number };

    let isError = false;
    if(data?.data?.errors?.certificates_attributes) {
      isError = true;
      try {
        let errors: string[] = ["Fehler beim Speichern des Zertifikats:"];
        for (const key in data.data.errors.certificates_attributes) {
          const errorGroup = data.data.errors.certificates_attributes[key];
          for (const field in errorGroup) {
            for(const message in errorGroup[field]) {
              if(errors.includes(errorGroup[field][message])) continue;
              errors.push(errorGroup[field][message]);
            }   
          }
        }
        setValidationInfo(errors);
      } catch (e) {
        setValidationInfo(["Kann nicht mit einem anderen Anbieterzertifikat des gleichen Typs überlappen."]);
      }
    } else if (status >= 400) {
      isError = true;
      setValidationInfo(["Fehler beim Speichern des Zertifikats (Status: " + status + ")."]);
    }

    if(isError) {
      if(isNewCertifacate === 1) dispatch(removeLastSellerCertificate());
      if(isNewCertifacate === 2) dispatch(removeLastCertificate());

      return;
    }
    navigate(`/${userRole}/verkaeuferliste/${seller_id ? seller_id : "verkaeufer_hinzufuegen"}`);
  };
  
  const pageTitle = isEditMode ? "Zertifikat bearbeiten" : "Zertifikat hinzufügen";
  const canSubmit = isFormValid && !!certificateTypeId && !!certificateNumber && !!logoRegNumber && !!validFrom && !!validTo;

  return (
    <Container
      maxWidth="xl"
      sx={{
        pt: 4,
        pb: 4,
        pl: { lg: 12, md: 14, xs: 13 },
      }}
    >
      <FormControl
        component="form"
        sx={{
          width: "100%",
        }}
        onSubmit={saveChanges}
      >
        <Header backButton={true} header={pageTitle} />
        <Grid container spacing={2} sx={{ mb: 2, mt: 2 }}>
          <Grid xs={12}>
            <CertificateForm
              certificate={certificateTypeId}
              certificateNumber={certificateNumber}
              logoRegNumber={logoRegNumber}
              validFrom={validFrom}
              validTo={validTo}
              handleCertificateChange={setCertificateType}
              handleCertificateNumberChange={setCertificateNumber}
              handleLogoRegNumberChange={setLogoRegNumber}
              handleValidFromChange={setValidFrom}
              handleValidToChange={setValidTo}
              onValidationChange={handleFormValidationChange}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
          <LotOfferButton
            type="submit"
            label="Änderungen speichern"
            sx={{ borderRadius: "26px", height: "51px", fontWeight: "bold" }}
            onClick={saveChanges}
            disabled={!canSubmit}
          />
          <OutlinedButton onClick={() => {
            navigate(-1);
          }}>
            Änderungen VERWERFEN
          </OutlinedButton>
        </Box>
      </FormControl>
      <Backdrop sx={{ backgroundColor: "#grey", zIndex: 1200 }} open={validationInfo.length > 0} onClick={() => setValidationInfo([])}>
        <ValidationErrors
          checked={validationInfo.length > 0}
          handleClose={() => setValidationInfo([])}
          text={validationInfo}
        />
      </Backdrop>
    </Container>
  );
}